import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Check,
  FileCheck2,
  Layers3,
  Mail,
  PlayCircle,
  PlugZap,
  Send,
  ShieldCheck,
  Sparkles,
  X,
} from 'lucide-react';
import { haptic } from '../../telegram';
import { settingsApi } from '../../api/settingsApi';

type GuideSceneKind = 'task' | 'demo' | 'connect' | 'launch';

interface Slide {
  icon: typeof Sparkles;
  scene: GuideSceneKind;
  eyebrow: string;
  title: string;
  body: string;
  points: string[];
  example: string;
}

const SLIDES: Slide[] = [
  {
    icon: Sparkles,
    scene: 'task',
    eyebrow: 'Шаг 1 · Задача',
    title: 'Скажите, что хотите получить',
    body: 'Одной фразы достаточно. Не нужно разбираться в агентах, инструментах и настройках — оркестратор соберёт понятный план.',
    points: ['Текстом или голосом — как удобнее', 'Только один уточняющий вопрос, если без него нельзя'],
    example: '«Каждое утро показывай письма без ответа и три главных дела».',
  },
  {
    icon: Layers3,
    scene: 'demo',
    eyebrow: 'Шаг 2 · Решение',
    title: 'Увидьте результат до настройки',
    body: 'Сначала откройте безопасный пример. За несколько секунд станет понятно, что именно вы получите и подходит ли вам формат.',
    points: ['Вымышленные данные — ваши аккаунты не используются', 'Формат результата виден сразу, без установки'],
    example: 'Нажмите «Демо ответа» и пролистайте путь от запроса до готового результата.',
  },
  {
    icon: PlugZap,
    scene: 'connect',
    eyebrow: 'Шаг 3 · Подключения',
    title: 'Подключите только нужное',
    body: 'Платформа сама покажет минимальный набор подключений и объяснит каждое из них до авторизации.',
    points: ['Никаких лишних доступов', 'Конкретный аккаунт всегда выбираете вы'],
    example: 'Для утреннего брифа можно выбрать только рабочую почту и календарь.',
  },
  {
    icon: ShieldCheck,
    scene: 'launch',
    eyebrow: 'Шаг 4 · Запуск',
    title: 'Один контрольный клик',
    body: 'Проверьте краткое резюме и подтвердите запуск. Никаких скрытых действий: установка, выполнение и доставка имеют отдельные статусы.',
    points: ['До подтверждения ничего не отправляется и не изменяется', 'Первый результат появляется в понятном формате'],
    example: 'Проверьте действие, подтвердите его и получите результат там, где выбрали.',
  },
];

function GuideScene({ kind, Icon }: { kind: GuideSceneKind; Icon: typeof Sparkles }) {
  return (
    <div className={`rf-guide-scene rf-guide-scene-${kind}`} aria-hidden="true">
      <span className="rf-guide-scene-glow" />
      {kind === 'task' && (
        <>
          <div className="rf-guide-task-bubble">
            <span className="rf-guide-avatar"><Sparkles /></span>
            <span className="rf-guide-task-copy"><i /><i /><i /></span>
            <Send />
          </div>
          <div className="rf-guide-outcome-chip"><Check /> Утренний список готов</div>
          <span className="rf-guide-orbit"><Icon /></span>
        </>
      )}
      {kind === 'demo' && (
        <div className="rf-guide-demo-window">
          <div className="rf-guide-demo-head"><span><PlayCircle /> Безопасное демо</span><b>12 сек</b></div>
          <div className="rf-guide-demo-row is-one"><span>01</span><i /></div>
          <div className="rf-guide-demo-row is-two"><span>02</span><i /></div>
          <div className="rf-guide-demo-result"><FileCheck2 /><span><b>Результат готов</b><small>Понятный формат до установки</small></span><Check /></div>
        </div>
      )}
      {kind === 'connect' && (
        <div className="rf-guide-connect-map">
          <span className="rf-guide-connect-line line-one" />
          <span className="rf-guide-connect-line line-two" />
          <span className="rf-guide-connect-line line-three" />
          <span className="rf-guide-source source-mail"><Mail /><small>Почта</small></span>
          <span className="rf-guide-source source-calendar"><CalendarDays /><small>Календарь</small></span>
          <span className="rf-guide-source source-result"><FileCheck2 /><small>Отчёт</small></span>
          <span className="rf-guide-hub"><PlugZap /><i /></span>
        </div>
      )}
      {kind === 'launch' && (
        <div className="rf-guide-launch-card">
          <div className="rf-guide-launch-shield"><ShieldCheck /></div>
          <strong>Всё под вашим контролем</strong>
          <div className="rf-guide-launch-step"><span><Check /></span><i>Настройки проверены</i></div>
          <div className="rf-guide-launch-step"><span><Check /></span><i>Действие подтверждено</i></div>
          <div className="rf-guide-launch-ready"><Sparkles /> Результат доставлен</div>
        </div>
      )}
    </div>
  );
}

export function Guide() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [idx, setIdx] = useState(0);
  const [finishing, setFinishing] = useState(false);
  const total = SLIDES.length;
  const last = idx === total - 1;

  const markSeenAndExit = async (destination: '/' | '/solutions') => {
    setFinishing(true);
    try { await settingsApi.saveProfile({ guide_seen: true } as any); } catch {}
    qc.invalidateQueries({ queryKey: ['profile'] });
    nav(destination);
  };
  const next = () => {
    haptic('light');
    if (last) void markSeenAndExit('/solutions');
    else setIdx((i) => Math.min(i + 1, total - 1));
  };
  const prev = () => { haptic('light'); setIdx((i) => Math.max(i - 1, 0)); };
  const skip = () => { haptic('medium'); void markSeenAndExit('/'); };

  const slide = SLIDES[idx];
  const Icon = slide.icon;

  return (
    <div className="rf-detail rf-guide">
      <header className="rf-topbar rf-guide-topbar">
        <button className="rf-back" onClick={skip} disabled={finishing}><X />Закрыть</button>
        <div className="rf-topbar-title">Как всё работает</div>
        <span className="rf-guide-counter">{idx + 1}/{total}</span>
      </header>

      <main className="rf-detail-scroll rf-guide-scroll">
        <div className="rf-guide-progress" aria-label={`Шаг ${idx + 1} из ${total}`}>
          <div className="rf-guide-bar"><span style={{ width: `${((idx + 1) / total) * 100}%` }} /></div>
          <div className="rf-guide-dots" aria-hidden="true">{SLIDES.map((_, i) => <span key={i} className={i <= idx ? 'active' : ''} />)}</div>
        </div>

        <article className="rf-guide-card" key={slide.scene} aria-live="polite">
          <div className="rf-guide-visual">
            <span className="rf-guide-visual-step">0{idx + 1}</span>
            <GuideScene kind={slide.scene} Icon={Icon} />
          </div>
          <div className="rf-guide-copy">
            <span className="rf-guide-eyebrow">{slide.eyebrow}</span>
            <h1>{slide.title}</h1>
            <p className="rf-guide-body">{slide.body}</p>
            <ul className="rf-guide-points">{slide.points.map((point) => <li key={point}><span><Check /></span>{point}</li>)}</ul>
            <aside className="rf-guide-example"><PlayCircle /><div><strong>Пример</strong><p>{slide.example}</p></div></aside>
          </div>
        </article>
      </main>

      <nav className="rf-guide-nav" aria-label="Навигация по экскурсии">
        {idx === 0
          ? <button className="rf-btn rf-guide-secondary" onClick={skip} disabled={finishing}>Не сейчас</button>
          : <button className="rf-btn rf-guide-secondary" onClick={prev} disabled={finishing}><ArrowLeft />Назад</button>}
        <button className="rf-btn primary rf-guide-next" onClick={next} disabled={finishing}>
          {last ? <><Check />К готовым решениям</> : <>Дальше<ArrowRight /></>}
        </button>
      </nav>
    </div>
  );
}
